import { Breadcrumb, SessionContext } from '../types';

const MAX_BREADCRUMBS = 20;
const breadcrumbs: Breadcrumb[] = [];
let sessionStartTime: Date | null = null;
let isInitialized = false;
let popstateHandler: (() => void) | null = null;

export function initFeedbackTracker(): void {
  if (isInitialized) return;

  sessionStartTime = new Date();
  isInitialized = true;

  document.addEventListener('click', handleClick, true);

  popstateHandler = () => {
    addBreadcrumb('navigate', window.location.pathname, 'browser navigation');
  };
  window.addEventListener('popstate', popstateHandler);

  addBreadcrumb('navigate', window.location.pathname, 'page load');
}

export function destroyFeedbackTracker(): void {
  document.removeEventListener('click', handleClick, true);
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler);
    popstateHandler = null;
  }
  isInitialized = false;
  sessionStartTime = null;
  breadcrumbs.length = 0;
}

function handleClick(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  if (!target) return;

  if (target.closest('[data-feedback-fab]')) return;

  const clickable = target.closest(
    'button, a, [role="button"], [onclick], input[type="submit"]',
  ) as HTMLElement;
  if (!clickable) return;

  const targetInfo = getElementDescription(clickable);
  const details = getClickDetails(clickable);

  addBreadcrumb('click', targetInfo, details);
}

function getElementDescription(element: HTMLElement): string {
  const testId = element.getAttribute('data-testid');
  if (testId) return `[data-testid="${testId}"]`;

  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel.slice(0, 50);

  const text = element.textContent?.trim();
  if (text && text.length < 50) return text;
  if (text) return text.slice(0, 47) + '...';

  const tagName = element.tagName.toLowerCase();
  const id = element.id;
  if (id) return `${tagName}#${id}`;

  return tagName;
}

function getClickDetails(element: HTMLElement): string {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'a') {
    const href = element.getAttribute('href');
    if (href) return `href: ${href.slice(0, 50)}`;
  }

  if (tagName === 'button') {
    const type = element.getAttribute('type');
    if (type === 'submit') return 'form submit';
  }

  if (element.closest('nav')) {
    return 'navigation';
  }

  return '';
}

export function addBreadcrumb(
  action: Breadcrumb['action'],
  target?: string,
  details?: string,
): void {
  const breadcrumb: Breadcrumb = {
    timestamp: new Date().toISOString(),
    action,
    target: target?.slice(0, 100),
    details: details?.slice(0, 200),
  };

  breadcrumbs.push(breadcrumb);

  while (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

export function trackNavigation(route: string): void {
  addBreadcrumb('navigate', route);
}

export function trackFormSubmit(formName: string, details?: string): void {
  addBreadcrumb('submit', formName, details);
}

export function trackAction(target: string, details?: string): void {
  addBreadcrumb('custom', target, details);
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...breadcrumbs];
}

export function getSessionContext(currentRoute: string, authTime?: number): SessionContext {
  const now = new Date();
  let sessionStart = sessionStartTime;
  if (!sessionStart) {
    console.warn(
      'feedbackTracker: sessionStartTime was not initialized before getSessionContext was called. ' +
        'Ensure initFeedbackTracker() is called before using feedback features. Using current time as fallback.',
    );
    sessionStart = now;
  }
  const durationSeconds = Math.floor((now.getTime() - sessionStart.getTime()) / 1000);

  return {
    session_started_at: sessionStart.toISOString(),
    session_duration_seconds: durationSeconds,
    auth_time: authTime ? new Date(authTime * 1000).toISOString() : undefined,
    current_route: currentRoute,
  };
}

export function clearBreadcrumbs(): void {
  breadcrumbs.length = 0;
}
